import React, { useState } from 'react';

const StudentAnswers = ({ questions, title, googleFormUrl, googleFormEntries, googleFormEmbedUrl }) => {
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [showEmbed, setShowEmbed] = useState(false);

  const handleAnswerChange = (index, value) => {
    setAnswers(prev => ({
      ...prev,
      [index]: value
    }));
  };

  const buildContent = () => {
    let content = `${title}\n${'='.repeat(title.length)}\n\n`;
    
    questions.forEach((q, index) => {
      content += `Q${index + 1}: ${q.text}\n`;
      content += `A${index + 1}: ${answers[index] || '[No answer provided]'}\n\n`;
    });

    content += `---\nSubmitted on: ${new Date().toLocaleString()}\n`;
    return content;
  };

  const downloadAnswers = () => {
    const content = buildContent();
    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `answers-${title.toLowerCase().replace(/\s+/g, '-')}-${Date.now()}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const copyAnswers = () => {
    navigator.clipboard.writeText(buildContent()).then(() => {
      alert('Answers copied to clipboard!');
    });
  };

  const submitToGoogleForms = async () => {
    if (!googleFormUrl || !googleFormEntries) {
      alert('Google Form not configured. Please contact your teacher.');
      return;
    }

    setSubmitting(true);
    setSubmitStatus('submitting');

    try {
      const formData = new FormData();
      
      questions.forEach((q, index) => {
        const entryId = googleFormEntries[index];
        if (entryId) {
          formData.append(entryId, answers[index] || '');
        }
      });

      formData.append('timestamp', new Date().toISOString());

      const response = await fetch(googleFormUrl, {
        method: 'POST',
        mode: 'no-cors',
        body: formData
      });

      setSubmitStatus('success');
      setTimeout(() => setSubmitStatus(null), 3000);
    } catch (error) {
      setSubmitStatus('error');
      console.error('Google Forms submission error:', error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="student-answers-section">
      <h2 className="student-answers-title">{title}</h2>
      
      {googleFormEmbedUrl && (
        <div className="google-form-embed-section">
          <button 
            className="btn-toggle-embed"
            onClick={() => setShowEmbed(!showEmbed)}
          >
            {showEmbed ? 'Hide Google Form' : 'Open Google Form'}
          </button>
          
          {showEmbed && (
            <div className="google-form-iframe-wrapper">
              <iframe
                src={googleFormEmbedUrl}
                width="100%"
                height="800"
                frameBorder="0"
                marginHeight="0"
                marginWidth="0"
                title="Student Introduction Form"
              >Loading…</iframe>
            </div>
          )}
        </div>
      )}

      {!googleFormEmbedUrl || !showEmbed ? (
        <>
          <p className="student-answers-instruction">
            Answer each question below. Click <strong>Submit to Teacher</strong> to send your answers directly, or download a copy for yourself.
          </p>
          
          <div className="questions-container">
            {questions.map((q, index) => (
              <div key={index} className="question-answer-block">
                <label htmlFor={`answer-${index}`} className="question-label">
                  Question {index + 1}: {q.text}
                </label>
                <textarea
                  id={`answer-${index}`}
                  className="answer-textarea"
                  placeholder="Type your answer here..."
                  value={answers[index] || ''}
                  onChange={(e) => handleAnswerChange(index, e.target.value)}
                  rows={3}
                />
              </div>
            ))}
          </div>

          <div className="answers-actions">
            {googleFormUrl && googleFormEntries && (
              <button 
                onClick={submitToGoogleForms} 
                className="btn-primary btn-submit"
                disabled={submitting}
              >
                {submitting ? 'Submitting...' : submitStatus === 'success' ? 'Submitted!' : 'Submit to Teacher'}
              </button>
            )}
            <button onClick={downloadAnswers} className="btn-secondary">
              Download Copy
            </button>
            <button onClick={copyAnswers} className="btn-secondary">
              Copy to Clipboard
            </button>
          </div>

          {submitStatus === 'success' && (
            <p className="submit-success">✓ Answers submitted successfully! You can view them in your Google Sheets.</p>
          )}
          {submitStatus === 'error' && (
            <p className="submit-error">✗ Submission failed. Please try again or download a copy.</p>
          )}
        </>
      ) : (
        <p className="student-answers-instruction">
          Fill out the form above. Your responses will go directly to your teacher's Google Sheets.
        </p>
      )}
    </div>
  );
};

export default StudentAnswers;